/** Imperative confirm/prompt/alert backed by the shared Dialog. Replaces
 *  native window.confirm/prompt/alert so dialogs match the app's styling and
 *  are accessible. Render <DialogHost/> once at the app root. */
import {makeAutoObservable, runInAction} from "mobx";

export type DialogKind = "alert" | "confirm" | "prompt";

interface BaseOptions {
    title?: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    danger?: boolean;
}

interface PromptOptions extends BaseOptions {
    defaultValue?: string;
    placeholder?: string;
}

export interface DialogRequest {
    kind: DialogKind;
    title?: string;
    message?: string;
    confirmLabel: string;
    cancelLabel: string;
    danger: boolean;
    defaultValue: string;
    placeholder?: string;
    resolve: (value: unknown) => void;
}

class DialogStoreClass {
    current: DialogRequest | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    private open(req: Omit<DialogRequest, "resolve">): Promise<unknown> {
        return new Promise((resolve) => {
            runInAction(() => {
                this.current = {...req, resolve};
            });
        });
    }

    alert(opts: BaseOptions): Promise<void> {
        return this.open({
            kind: "alert", title: opts.title, message: opts.message,
            confirmLabel: opts.confirmLabel ?? "OK", cancelLabel: "",
            danger: false, defaultValue: "",
        }) as Promise<void>;
    }

    confirm(opts: BaseOptions): Promise<boolean> {
        return this.open({
            kind: "confirm", title: opts.title, message: opts.message,
            confirmLabel: opts.confirmLabel ?? "Confirm", cancelLabel: opts.cancelLabel ?? "Cancel",
            danger: !!opts.danger, defaultValue: "",
        }) as Promise<boolean>;
    }

    prompt(opts: PromptOptions): Promise<string | null> {
        return this.open({
            kind: "prompt", title: opts.title, message: opts.message,
            confirmLabel: opts.confirmLabel ?? "OK", cancelLabel: opts.cancelLabel ?? "Cancel",
            danger: !!opts.danger, defaultValue: opts.defaultValue ?? "", placeholder: opts.placeholder,
        }) as Promise<string | null>;
    }

    /** Resolve the active request and close. */
    resolve(value: unknown): void {
        const req = this.current;
        runInAction(() => {
            this.current = null;
        });
        req?.resolve(value);
    }
}

const DialogStore = new DialogStoreClass();
export default DialogStore;
